import { type FetchResult, ApolloError, useApolloClient } from "@apollo/client";
import type { GraphQLFormattedError } from "graphql";
import React from "react";

import type {
  JobSeekerProfileFile,
  JobSeekerProfileResumeType,
} from "../../../../__generated__/types.gqlgenerated";
import { useLocale, useSafeI18n } from "../../../I18n";
import { fetchDualResumesEnabled } from "../../../lib/DualResume/useDualResumesEnabled";
import {
  type AddJobSeekerProfileResumeFileMutation,
  useAddJobSeekerProfileResumeFileMutation,
} from "../graphql/__generated__/addResumeFile.gqlgenerated";
import { useGetJobSeekerProfileUploadResumeUrlMutation } from "../graphql/__generated__/getResumeUploadUrl.gqlgenerated";
import {
  type SharedUploaderJobSeekerResumesQuery,
  SharedUploaderJobSeekerResumesDocument,
  useSharedUploaderDeleteResumeBundleMutation,
} from "../graphql/__generated__/resumeBundles.gqlgenerated";
import {
  extractPfsvErrorCode,
  hasNonParsingCreateError,
  isBundleAlreadyDeletedError,
  ResumeReplaceDeleteError,
  ResumeReplacePostDeleteCreateError,
  ResumeUploadErrorStage,
} from "./resumeQuotaErrors";
import type { SelectedFile } from "./SelectedFile";
import { selectUploadedResumeBundle } from "./selectResumeBundle";
import { useS3Upload } from "./useS3Upload";

export { ResumeReplaceDeleteError, ResumeReplacePostDeleteCreateError, ResumeUploadErrorStage };

type UseResumeFileUploadOptions = {
  refetchQueries?: NonNullable<
    Parameters<typeof useAddJobSeekerProfileResumeFileMutation>[0]
  >["refetchQueries"];
};

export interface StagedResumeFile {
  /** File metadata and bytes retained by the client until the upload is committed. */
  file: SelectedFile;
  /** ID of the file uploaded to temporary storage. */
  fileId: string;
}

const PARSE_FAILED_TAG = "parse:failed";
const hasParseFailedTag = (tags: readonly string[] | undefined | null): boolean =>
  !!tags?.includes(PARSE_FAILED_TAG);

export const isUnparseableFile = (fileInformation: JobSeekerProfileFile | undefined) =>
  hasParseFailedTag(fileInformation?.tags);

// Twin of isUnparseableFile for resolved resume shapes that are not JobSeekerProfileFile.
export const isUnparseableResume = (resume: { tags?: readonly string[] } | undefined | null) =>
  hasParseFailedTag(resume?.tags);

enum UploadStage {
  IDLE = "IDLE",
  GETTING_URL = "GETTING_URL",
  UPLOADING_TO_S3 = "UPLOADING_TO_S3",
  CLEARING_COLLECTION = "CLEARING_COLLECTION",
  LINKING_UPLOAD = "LINKING_UPLOAD",
  COMPLETE = "COMPLETE",
}

// The flag read, bundle read and delete run outside every mutation hook, so `loading` has to
// come from the stage or callers see not-loading mid-upload and allow a second submit.
// UPLOADING_TO_S3 is deliberately absent. useS3Upload already reports it through uploadLoading,
// and stageResumeFile returns while still in that stage, so including it would pin loading true
// for the whole window between staging and a deferred commit.
const IN_FLIGHT_STAGES: ReadonlySet<UploadStage> = new Set([
  UploadStage.GETTING_URL,
  UploadStage.CLEARING_COLLECTION,
  UploadStage.LINKING_UPLOAD,
]);

/**
 * Calculates a user-friendly progress percentage using both the graphql calls and upload together.
 * Progress breakdown:
 * - IDLE: 0%
 * - GETTING_URL: 10%
 * - UPLOADING_TO_S3: 25% + (s3Progress * 0.65) - pro-rated between 25% and 90%
 * - CLEARING_COLLECTION: 90%
 * - LINKING_UPLOAD: 90%
 * - COMPLETE: 100%
 */
function calculateProgress(stage: UploadStage, s3Progress: number): number {
  switch (stage) {
    case UploadStage.IDLE:
      return 0;
    case UploadStage.GETTING_URL:
      return 10;
    case UploadStage.UPLOADING_TO_S3:
      // Pro-rate S3 progress between 25% and 90%
      return 25 + (s3Progress / 100) * 65;
    case UploadStage.CLEARING_COLLECTION:
    case UploadStage.LINKING_UPLOAD:
      return 90;
    case UploadStage.COMPLETE:
      return 100;
    default:
      return 0;
  }
}

export function useResumeFileUpload({ refetchQueries = [] }: UseResumeFileUploadOptions = {}) {
  const { uploadFileToS3, loading: uploadLoading, progress: s3Progress } = useS3Upload();
  const [uploadStage, setUploadStage] = React.useState<UploadStage>(UploadStage.IDLE);
  const [getUrlMutation, { loading: getUrlMutationLoading }] =
    useGetJobSeekerProfileUploadResumeUrlMutation();
  const apolloClient = useApolloClient();
  const [deleteResumeBundle] = useSharedUploaderDeleteResumeBundleMutation();
  const [addFileMutation, { loading: addFileMutationLoading }] =
    useAddJobSeekerProfileResumeFileMutation({
      refetchQueries,
      awaitRefetchQueries: true,
      errorPolicy: "all",
    });
  const { gettext } = useSafeI18n();
  const { country } = useLocale();
  const countryCode = country?.toUpperCase() ?? "UNKNOWN";

  const progress = React.useMemo(
    () => calculateProgress(uploadStage, s3Progress),
    [uploadStage, s3Progress],
  );

  const resetProgress = React.useCallback(() => {
    // Ignored mid-flight. loading is derived from the stage now, so clearing it here would
    // report not-loading while the delete is still running and let a second upload start.
    // Every in-flight stage resolves itself, so nothing stays stuck.
    setUploadStage((current) => (IN_FLIGHT_STAGES.has(current) ? current : UploadStage.IDLE));
  }, []);

  const stageResumeFile = React.useCallback(
    async (selectedFile: SelectedFile): Promise<StagedResumeFile> => {
      try {
        setUploadStage(UploadStage.GETTING_URL);

        const { data } = await getUrlMutation();
        const uploadUrl = data?.createJobSeekerProfileUploadResumeFileTempUrl?.uploadURL;
        const fileId = data?.createJobSeekerProfileUploadResumeFileTempUrl?.fileId;

        if (!uploadUrl || !fileId) {
          throw new Error("Failed to get upload URL and file ID");
        }

        setUploadStage(UploadStage.UPLOADING_TO_S3);
        await uploadFileToS3(uploadUrl, selectedFile);

        return { file: selectedFile, fileId };
      } catch (error) {
        console.error("[useResumeFileUpload] Upload failed:", error);
        setUploadStage(UploadStage.IDLE);
        throw error;
      }
    },
    [getUrlMutation, uploadFileToS3],
  );

  const commitStagedResumeFile = React.useCallback(
    async (stagedResumeFile: StagedResumeFile, resumeType?: JobSeekerProfileResumeType) => {
      // Hoisted so the catch can classify. Once the bundle is gone, any later failure leaves
      // the seeker with no resume.
      let deletedExistingBundle = false;
      let failedAddPfsvCode: string | undefined;

      try {
        const { file: selectedFile, fileId } = stagedResumeFile;

        const variables = {
          input: {
            fileId,
            geoCountry: countryCode,
            ipCountry: countryCode,
            mediaType: selectedFile.mimeType,
            name: selectedFile.name,
            description: gettext("Resume file uploaded from Indeed Profile"),
            resumeType,
          },
        };

        setUploadStage(UploadStage.CLEARING_COLLECTION);

        // Resolved here rather than at mount because creating a resume can flip the flag.
        // Undefined means the read failed, which must not be treated as confirmation.
        const dualResumeFlags = await fetchDualResumesEnabled(apolloClient);

        // PFSV appends rather than replaces and caps uploaded resumes at one, so clear the
        // collection first. Only on a confirmed dual-rez account, because a backfilled bundle
        // shares its permanent file with the legacy record and the delete removes that file.
        const { data: bundleData } = dualResumeFlags?.isDualResumesEnabled
          ? await apolloClient.query<SharedUploaderJobSeekerResumesQuery>({
              query: SharedUploaderJobSeekerResumesDocument,
              // This slim selection would overwrite the jobSeekerResumes cache field that
              // richer queries read from.
              fetchPolicy: "no-cache",
            })
          : { data: undefined };

        const existingBundleId = selectUploadedResumeBundle(
          bundleData?.jobSeekerResumes?.resumes,
        )?.resumeId;

        if (existingBundleId) {
          // A rejected delete leaves the server outcome unknown, so report it as a delete
          // failure rather than a generic upload error.
          let deleteErrors: readonly GraphQLFormattedError[] | undefined;
          try {
            ({ errors: deleteErrors } = await deleteResumeBundle({
              variables: { input: { resumeId: existingBundleId } },
            }));
          } catch (deleteError) {
            throw new ResumeReplaceDeleteError(
              deleteError instanceof Error ? deleteError.message : String(deleteError),
            );
          }
          if (deleteErrors?.length && !isBundleAlreadyDeletedError(deleteErrors)) {
            throw new ResumeReplaceDeleteError(
              deleteErrors.map((deleteError) => deleteError.message).join(", "),
            );
          }
          deletedExistingBundle = true;

          // Consumers that pass no refetchQueries would keep reading the bundle we just
          // deleted, so drop the field and let their watchers refetch.
          apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "jobSeekerResumes" });
          apolloClient.cache.gc();
        }

        setUploadStage(UploadStage.LINKING_UPLOAD);
        const result: FetchResult<AddJobSeekerProfileResumeFileMutation> = await addFileMutation({
          variables,
        });

        // Parse failures are not create errors. Return the partial data so pages can show the
        // file card with an unparseable-file message.
        if (result.errors?.length && hasNonParsingCreateError(result.errors)) {
          // Recorded before the throw so the catch can attach it without the raw messages.
          failedAddPfsvCode = extractPfsvErrorCode(result.errors);
          throw new ApolloError({ graphQLErrors: result.errors });
        }

        if (!result.data?.addJobSeekerProfileResumeFile?.originalResumeFile?.id) {
          const missingFileMessage =
            "uploadFile mutation call did not return an originalResumeFile";
          // An error-free create returning nothing usable is still a failed create.
          if (deletedExistingBundle) {
            throw new ResumeReplacePostDeleteCreateError(missingFileMessage, failedAddPfsvCode);
          }
          throw new Error(missingFileMessage);
        }

        setUploadStage(UploadStage.COMPLETE);
        return result;
      } catch (error) {
        console.error("[useResumeFileUpload] Upload failed:", error);
        setUploadStage(UploadStage.IDLE);

        // Anything raised after the delete committed is a destructive window, including
        // Apollo rejecting on a transport failure rather than returning errors.
        if (
          deletedExistingBundle &&
          !(error instanceof ResumeReplaceDeleteError) &&
          !(error instanceof ResumeReplacePostDeleteCreateError)
        ) {
          throw new ResumeReplacePostDeleteCreateError(
            error instanceof Error ? error.message : String(error),
            failedAddPfsvCode,
          );
        }
        throw error;
      }
    },
    [addFileMutation, apolloClient, countryCode, deleteResumeBundle, gettext],
  );

  const uploadResumeFile = React.useCallback(
    async (selectedFile: SelectedFile, resumeType?: JobSeekerProfileResumeType) => {
      const stagedResumeFile = await stageResumeFile(selectedFile);
      return commitStagedResumeFile(stagedResumeFile, resumeType);
    },
    [commitStagedResumeFile, stageResumeFile],
  );

  return {
    uploadResumeFile,
    stageResumeFile,
    commitStagedResumeFile,
    resetProgress,
    loading:
      getUrlMutationLoading ||
      addFileMutationLoading ||
      uploadLoading ||
      IN_FLIGHT_STAGES.has(uploadStage),
    progress,
  };
}
